import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, share, startWith, tap } from 'rxjs/operators';
import { AdvertiserBannerHttpService } from 'src/app/http-services/advertiser-banner-http.service';
import { LogService } from 'src/app/shared/log/log.service';
import { LogType } from 'src/app/shared/log/log.service.types';
import { isNotEmpty } from 'src/app/shared/rx.helpers';
import { BannerCommon } from 'src/app/shared/types/banner-common.types';
import { IBannerDisplay } from 'src/app/shared/types/banner.types';
import ModerateStatus = BannerCommon.ModerateStatus;

export enum BannerAcceptStatuses {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
}

interface IBannerWrapper {
  banner: IBannerDisplay;
  status$: Observable<BannerAcceptStatuses>;
}

interface IAutoAcceptBannersState {
  isShown: boolean;
  banners?: IBannerWrapper[];
}

const initState: IAutoAcceptBannersState = {
  isShown: true,
}

const FETCH_BANNERS_DELAY = 8_000;
const bannersStatusesMapper = (banners: IBannerWrapper[] | undefined) => banners?.map(banner => banner.status$);

@Injectable()
export class AutoAcceptBannersStore extends ComponentStore<IAutoAcceptBannersState> {
  public readonly isShown$: Observable<boolean> = this.select(state => state.isShown);
  public readonly banners$: Observable<IBannerWrapper[] | undefined> = this.select(state => state.banners);

  public readonly progress$: Observable<number> = this.select(
    this.banners$,
    bannersStatusesMapper,
  )
    .pipe(
      mergeMap(bannerAcceptStatuses => bannerAcceptStatuses ? forkJoin([...bannerAcceptStatuses]) : of([])),
      // eslint-disable-next-line @ngrx/avoid-mapping-component-store-selectors
      map((acceptStatuses = []) => {
        if (acceptStatuses.length === 0) {
          return 5;
        }

        const finishedStatuses = acceptStatuses.filter(status => status !== BannerAcceptStatuses.PENDING);
        return Math.round(100 * finishedStatuses.length / acceptStatuses.length);
      }),
    );

  public readonly areAllBannersFinished$: Observable<[boolean, boolean]> = this.select(
    this.banners$,
    bannersStatusesMapper,
  )
    .pipe(
      filter(isNotEmpty),
      mergeMap(bannerAcceptStatuses => forkJoin([...bannerAcceptStatuses])),
      filter(acceptStatuses => !acceptStatuses.includes(BannerAcceptStatuses.PENDING)),
      // eslint-disable-next-line @ngrx/avoid-mapping-component-store-selectors
      map(acceptStatuses => [
        acceptStatuses.includes(BannerAcceptStatuses.SUCCESS),
        acceptStatuses.includes(BannerAcceptStatuses.FAILED),
      ]),
    );

  public readonly fetchBanners = this.effect((event$: Observable<number>) => event$.pipe(
    delay(FETCH_BANNERS_DELAY),
    mergeMap(campaignId => this.advertiserBannerHttpService.getBanners(campaignId)),
    tap(banners => this.patchState({
      banners: banners.map(banner => {
        const changeStatus$ = banner.moderateStatus === ModerateStatus.ACCEPTED
          ? of(void 0)
          : this.advertiserBannerHttpService.changeStatus({ bannerId: banner.id!, bannerModerateStatus: ModerateStatus.ACCEPTED });

        return {
          banner,
          status$: changeStatus$
            .pipe(
              startWith(BannerAcceptStatuses.PENDING),
              map(status => status === BannerAcceptStatuses.PENDING ? BannerAcceptStatuses.PENDING : BannerAcceptStatuses.SUCCESS),
              catchError(error => {
                const formattedError = this.logService.createErrorInstance(error);

                this.logService.captureException(formattedError, {
                  tags: {
                    userType: LogType.ADVERTISER,
                    component: 'AutoAcceptBanners',
                  },
                  extra: { bannerId: banner.id },
                }, true);

                return of(BannerAcceptStatuses.FAILED);
              }),
              share(),
            ),
        };
      }),
    }))
  ));

  public readonly setIsShown = this.updater(
    (state, isShown: boolean): IAutoAcceptBannersState => ({
      ...state,
      isShown,
    })
  );

  private readonly advertiserBannerHttpService = inject(AdvertiserBannerHttpService);
  private readonly logService = inject(LogService);

  constructor() {
    super(initState);
  }
}
